import { createContext, useContext, useState, useCallback, ReactNode } from "react";

export type EntityType = "container" | "shipper" | "receiver" | "account" | "address" | "document" | null;

interface CommandMenuContextType {
    isOpen: boolean;
    open: (force?: boolean) => void;
    close: () => void;
    toggle: () => void;
    selectedEntity: EntityType;
    setSelectedEntity: (entity: EntityType) => void;
    shouldPreventAutoOpen: boolean;
}

const CommandMenuContext = createContext<CommandMenuContextType | undefined>(undefined);

export const CommandMenuProvider = ({ children }: { children: ReactNode }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState<EntityType>(null);
    const [shouldPreventAutoOpen, setShouldPreventAutoOpen] = useState(false);

    const open = useCallback((force = false) => {
        // Se è stato preventato l'apertura automatica e non è forzata, non aprire
        if (shouldPreventAutoOpen && !force) {
            return;
        }
        setIsOpen(true);
        // Se è forzata (click esplicito), reset il flag di prevenzione
        if (force) {
            setShouldPreventAutoOpen(false);
        }
    }, [shouldPreventAutoOpen]);

    const close = useCallback(() => {
        setShouldPreventAutoOpen(true);
        setIsOpen(false);
        // Reset filtro quando si chiude il menu
        setSelectedEntity(null);
        // Previeni l'apertura automatica per 200ms dopo la chiusura
        setTimeout(() => {
            setShouldPreventAutoOpen(false);
        }, 1000);
    }, []);

    const toggle = useCallback(() => {
        setIsOpen((prev) => !prev);
        if (isOpen) {
            setSelectedEntity(null);
        }
    }, [isOpen]);

    return (
        <CommandMenuContext.Provider
            value={{
                isOpen,
                open,
                close,
                toggle,
                selectedEntity,
                setSelectedEntity,
                shouldPreventAutoOpen,
            }}
        >
            {children}
        </CommandMenuContext.Provider>
    );
};

export const useCommandMenu = () => {
    const context = useContext(CommandMenuContext);
    if (context === undefined) {
        throw new Error("useCommandMenu must be used within a CommandMenuProvider");
    }
    return context;
};

